// Base DetailsDisclosure class (if not already defined)
if (!customElements.get('details-disclosure')) {
  class DetailsDisclosure extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');
    }

    close() {
      this.mainDetailsToggle.removeAttribute('open');
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);
}

// Enhanced ShareButton class
if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends DetailsDisclosure {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('button'),
          shareSummary: this.querySelector('summary'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input'),
          // Look for social buttons in the entire component, not just fallback
          socialButtons: this.querySelectorAll('.share-button__social-btn'),
        };
        
        this.urlToShare = this.elements.urlInput ? this.elements.urlInput.value : document.location.href;
        this.titleToShare = document.title;

        // Initialize social sharing for buttons outside details
        this.initializeSocialSharing();

        // Native sharing support
        if (navigator.share) {
          this.mainDetailsToggle.setAttribute('hidden', '');
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', () => {
            navigator.share({ 
              url: this.urlToShare, 
              title: this.titleToShare 
            });
          });
        } else {
          // Show the details element for fallback
          this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
          
          // Add event listeners for copy and close buttons
          const copyButton = this.mainDetailsToggle.querySelector('.share-button__copy');
          if (copyButton) {
            copyButton.addEventListener('click', this.copyToClipboard.bind(this));
          }
          
          const closeButton = this.mainDetailsToggle.querySelector('.share-button__close');
          if (closeButton) {
            closeButton.addEventListener('click', this.close.bind(this));
          }
        }
      }

      initializeSocialSharing() {
        this.elements.socialButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            const platform = this.getSocialPlatform(button);
            this.shareToSocial(platform);
          });
        });
      }

      getSocialPlatform(button) {
        if (button.classList.contains('share-button__social-btn--facebook')) {
          return 'facebook';
        } else if (button.classList.contains('share-button__social-btn--twitter')) {
          return 'twitter';
        } else if (button.classList.contains('share-button__social-btn--linkedin')) {
          return 'linkedin';
        }
      }

      shareToSocial(platform) {
        const encodedUrl = encodeURIComponent(this.urlToShare);
        const encodedTitle = encodeURIComponent(this.titleToShare);
        let shareUrl = '';

        switch (platform) {
          case 'facebook':
            shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
            break;
          case 'twitter':
            shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
            break;
          case 'linkedin':
            shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
            break;
        }

        if (shareUrl) {
          window.open(shareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
        }
      }

      toggleDetails() {
        if (!this.mainDetailsToggle.open) {
          this.elements.successMessage.classList.add('hidden');
          this.elements.successMessage.textContent = '';
          this.elements.closeButton.classList.add('hidden');
          this.elements.shareSummary.focus();
        }
      }

      copyToClipboard() {
        if (navigator.clipboard && window.isSecureContext) {
          // Modern clipboard API
          navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
            this.showSuccessMessage();
          }).catch(() => {
            this.fallbackCopyToClipboard();
          });
        } else {
          // Fallback for older browsers or non-secure contexts
          this.fallbackCopyToClipboard();
        }
      }

      fallbackCopyToClipboard() {
        try {
          this.elements.urlInput.select();
          this.elements.urlInput.setSelectionRange(0, 99999); // For mobile devices
          document.execCommand('copy');
          this.showSuccessMessage();
        } catch (err) {
          console.error('Failed to copy: ', err);
          // Show a message asking user to copy manually
          this.elements.successMessage.classList.remove('hidden');
          this.elements.successMessage.textContent = 'Please copy the URL manually';
          this.elements.closeButton.classList.remove('hidden');
        }
      }

      showSuccessMessage() {
        const successText = window.accessibilityStrings?.shareSuccess || 'Link copied to clipboard!';
        this.elements.successMessage.classList.remove('hidden');
        this.elements.successMessage.textContent = successText;
        this.elements.closeButton.classList.remove('hidden');
        this.elements.closeButton.focus();
      }

      updateUrl(url) {
        this.urlToShare = url;
        if (this.elements.urlInput) {
          this.elements.urlInput.value = url;
        }
      }

      updateTitle(title) {
        this.titleToShare = title;
      }
    }
  );
}